/* eslint-disable no-prototype-builtins */
import * as React from 'react';
import currency from 'currency.js';

import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import MoreButton from 'components/buttons/more-button/MoreButton';
import CreatorInfoWithChannels from 'components/creator-info-with-channels/CreatorInfoWithChannels';

import CostAttributionTaskCounter from './counters/CostAttributionTaskCounter';
import CostAttributionTileInput, { SUPPORTED_ATTRIBUTION_DELIVERABLES } from './input/CostAttributionTileInput';

import { insertIf } from 'utils/list';
import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';
import { isInfluencerProfileRejected } from 'utils/profile';
import { DELIVERABLE_ADDITIONAL_COSTS, DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION } from 'utils/deliverables';
import { prepareDataForMetricsChannelsBasicFromCreator } from 'utils/metrics';
import { calculateTotalCCACosts } from 'modules/reporting/utils/cca-costs';

import type { CCADeliverableDescription } from './counters/CostAttributionTaskCounter';
import type { BudgetSplitCreatorResource } from '../../types/BudgetSplitResource.type';

import './cost-attribution-tile.scss';

applyTranslations(require('./cost-attribution-tile.i18n.json'));

type CostAttributionState = {
  [x: string]: string
};

type Props = {
  creator: BudgetSplitCreatorResource,
  totalAgreedBudget: number,
  splits: CostAttributionState,
  deliverables: Array<CCADeliverableDescription>,
  linkToMessenger: string,
  linkToOffer: string,
  onChange: (a: CostAttributionState) => void
};

const GROUPS = ['youtube', 'instagram', 'tiktok', 'blog', 'other', DELIVERABLE_ADDITIONAL_COSTS];

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  containerClassName: 'cost-attribution-tile__context-menu',
  forceEventPropagation: false
});

const CostAttributionTile = (
  {
    creator,
    totalAgreedBudget,
    deliverables,
    splits,
    linkToMessenger,
    linkToOffer,
    onChange
  }: Props
): JSX.Element => {
  const groupedDeliverables = deliverables.reduce((result, item) => ({
    ...result,
    [String(item.deliverableType)]: item.count
  }), {});

  const [costAttribution, setCostAttribution] = React.useState<CostAttributionState>(splits);
  const [lastEditedType, setLastEditedType] = React.useState<string>('');

  const totalEnteredBudget = Object.keys(costAttribution)
    .reduce(calculateTotalCCACosts(costAttribution), currency(0))
    .value;
  const isExceededBudget = totalEnteredBudget > totalAgreedBudget;
  const isBelowTheBudget = totalEnteredBudget < totalAgreedBudget;
  const budgetLeft = currency(totalAgreedBudget).subtract(currency(totalEnteredBudget)).value;
  const hasEnteredAdditionalCosts = costAttribution[DELIVERABLE_ADDITIONAL_COSTS]
    && costAttribution[DELIVERABLE_ADDITIONAL_COSTS] !== '0';
  const isInfluencerRejected = !isInfluencerProfileRejected(creator);

  const contextMenuItems = [
    ...insertIf(isInfluencerRejected, {
      name: 'messenger',
      text: __t('cost_attribution_tile.contextMenu.messenger'),
      link: linkToMessenger,
      openInNewTab: true
    }),
    ...insertIf(isInfluencerRejected, {
      type: 'separator'
    }),
    {
      name: 'offer',
      text: __t('cost_attribution_tile.contextMenu.offer'),
      link: linkToOffer,
      openInNewTab: true
    }
  ];

  return (
    <section className="cost-attribution-tile" data-scrollto={String(isBelowTheBudget)}>
      <div className="cost-attribution-tile__section cost-attribution-tile__title">
        <div className="cost-attribution-tile__creator-info">
          <CreatorInfoWithChannels
            creatorProfile={{
              ...creator,
              influencerFirstName: creator.name
            }}
            metricExtractor={prepareDataForMetricsChannelsBasicFromCreator}
            includeRatio
          />

          <MoreButtonWithMenu
            width={20}
            contextMenuProps={{ items: contextMenuItems }}
          />
        </div>

        <div className="cost-attribution-tile__price">
          {__t('cost_attribution_tile.total_budget_label')}
          <span className="cost-attribution-tile__price-highlight">{renderPrice(totalAgreedBudget)}</span>
        </div>

        <div className="cost-attribution-tile__tasks">
          <span className="cost-attribution-tile__tasks-label">{__t('cost_attribution_tile.tasks_label')}</span>
          <CostAttributionTaskCounter deliverables={deliverables} />
        </div>
      </div>

      <div
        className="cost-attribution-tile__section cost-attribution-tile__section--bottom cost-attribution-tile__distribution"
      >
        <div>
          <div className="cost-attribution-tile__inputs-title">
            {__t('cost_attribution_tile.distribute_budget_label')}
          </div>
          <div className="cost-attribution-tile__inputs">
            {
              SUPPORTED_ATTRIBUTION_DELIVERABLES.map((type: string) => {
                if (groupedDeliverables.hasOwnProperty(type) || type === DELIVERABLE_ADDITIONAL_COSTS) {
                  return (
                    <CostAttributionTileInput
                      key={type}
                      type={type}
                      value={costAttribution[type]}
                      isExceededBudget={lastEditedType === type && isExceededBudget}
                      isBelowTheBudget={isBelowTheBudget}
                      amount={groupedDeliverables[type] ?? 0}
                      onChange={(newValue: string) => {
                        const newAttribution = {
                          ...costAttribution,
                          [type]: newValue
                        };

                        setLastEditedType(type);
                        setCostAttribution(newAttribution);
                        onChange(newAttribution);
                      }}
                    />
                  );
                }

                return null;
              })
            }
            <CostAttributionTileInput
              key={DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION}
              type={DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION}
              value={costAttribution[DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION]}
              isExceededBudget={false}
              isBelowTheBudget={false}
              amount={0}
              canBeSkipped={!hasEnteredAdditionalCosts}
              mustBeEmpty={!hasEnteredAdditionalCosts}
              onChange={(newValue: string) => {
                const newAttribution = {
                  ...costAttribution,
                  [DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION]: newValue
                };

                setLastEditedType(DELIVERABLE_ADDITIONAL_COSTS_DESCRIPTION);
                setCostAttribution(newAttribution);
                onChange(newAttribution);
              }}
            />
          </div>
        </div>
        <div className="cost-attribution-tile__summary">
          <div className="cost-attribution-tile__summary-section cost-attribution-tile__summary-title">
            <span>{__t('cost_attribution_tile.summary_label')}</span>
            <span>{renderPrice(totalAgreedBudget)}</span>
          </div>
          {
            GROUPS
              // filter out those types which are not in deliverables list, but keep additional costs
              .filter((group) =>
                group === DELIVERABLE_ADDITIONAL_COSTS || Object.keys(groupedDeliverables).some(key => key.includes(group))
              )
              .map((group) => {
                const groupCost = Object.keys(costAttribution)
                  .filter(key => key.includes(group))
                  .reduce(calculateTotalCCACosts(costAttribution), currency(0))
                  .value;

                return (
                  <div
                    key={group}
                    className="cost-attribution-tile__summary-section cost-attribution-tile__summary-values"
                  >
                    <div className="cost-attribution-tile__summary-values--bold">
                      {__t(`cost_attribution_tile.group.${group}`)}
                    </div>
                    <div>
                      {groupCost ? renderPrice(groupCost) : '–'}
                    </div>
                  </div>
                );
              })
          }
          <div className="cost-attribution-tile__summary-subtotal">
            <div className="cost-attribution-tile__summary-values cost-attribution-tile__summary-values--bold">
              {renderPrice(totalEnteredBudget)}
            </div>
            <div className="cost-attribution-tile__summary-remaining">
              {__t('cost_attribution_tile.remaining_budget', {
                budgetLeft: renderPrice(budgetLeft)
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CostAttributionTile;
